import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, Copy, Download, Server, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useT, useLang } from "@/lib/i18n";

export default function Bridge() {
  const t = useT();
  const { lang } = useLang();
  const [keys, setKeys] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("local-machine");
  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/internal/bridge-keys"); setKeys(data.keys); }
    catch (e) { toast.error("Failed to load bridge keys"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/internal/bridge-keys", { name });
      setRevealed(data); setShowNew(false); setName("local-machine"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!confirm(lang === "pt" ? "Revogar essa bridge key?" : "Revoke this bridge key?")) return;
    await api.delete(`/internal/bridge-keys/${id}`);
    load();
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="overline mb-3">// bridge · self-hosted worker</div>
      <div className="flex justify-between items-end mb-2">
        <h1 className="font-display font-black text-4xl tracking-tighter">
          {lang === "pt" ? "Bridge Daemons" : "Bridge Daemons"}
        </h1>
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm py-2 px-4" data-testid="bridge-new-btn">
          <Plus className="w-4 h-4" /> {lang === "pt" ? "Gerar bridge key" : "Generate bridge key"}
        </button>
      </div>
      <p className="text-white/50 mb-8 text-sm max-w-2xl">
        {lang === "pt"
          ? "O Ratchet core roda na sua máquina/VM com HOME isolado por usuário. Esta página gera chaves para o ratchet-bridge.py se conectar e processar os cards reais. A IA é invocada via Claude CLI (claude login) — sem API key direta."
          : "The Ratchet core runs on your own machine/VM with per-user isolated HOME. This page generates keys for ratchet-bridge.py to connect and process real cards. AI is invoked via Claude CLI (claude login) — never a direct API key."}
      </p>

      {/* Quick-start */}
      <div className="surface p-6 mb-8" data-testid="bridge-quickstart">
        <div className="overline text-primary mb-3">{lang === "pt" ? "// quick start" : "// quick start"}</div>
        <ol className="space-y-2 text-sm text-white/70 list-decimal list-inside">
          <li>{lang === "pt" ? "Gere uma bridge key acima" : "Generate a bridge key above"}</li>
          <li>
            {lang === "pt" ? "Na sua máquina, autentique seu provider de IA:" : "On your machine, authenticate your AI provider:"}
            <pre className="mt-1 bg-black/40 p-2 font-mono text-[11px] inline-block ml-2">claude login</pre>
            <span className="text-white/40 text-[11px]"> · {lang === "pt" ? "OAuth para Claude · outros providers são roteados automaticamente" : "OAuth for Claude · other providers are routed automatically"}</span>
          </li>
          <li>
            {lang === "pt" ? "Baixe o script + contrato:" : "Download script + contract:"}{" "}
            <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/internal/bridge/script`} target="_blank" rel="noopener" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="bridge-download-script">
              <Download className="w-3 h-3" /> ratchet-bridge.py
            </a>
            {" · "}
            <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/internal/bridge/contract`} target="_blank" rel="noopener" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="bridge-download-contract">
              <ExternalLink className="w-3 h-3" /> contract docs
            </a>
          </li>
          <li>{lang === "pt" ? "Rode na sua máquina:" : "Run on your machine:"}</li>
        </ol>
        <pre className="mt-4 bg-black/40 p-4 font-mono text-xs leading-relaxed overflow-x-auto">{`# 1) Claude OAuth (default · recommended)
claude login

# 2) (optional) Other providers — bridge auto-routes via internal CLI
#    No raw API key required: OAuth, GitHub Models, Bedrock IAM, etc.

# 3) Connect bridge
export RATCHET_API_URL=${typeof window !== "undefined" ? window.location.origin : ""}
export RATCHET_BRIDGE_KEY=rkb_...
python3 ratchet-bridge.py`}</pre>
      </div>

      {/* AI providers · Claude OAuth or open-source CLI bridge */}
      <div className="surface p-6 mb-8" data-testid="bridge-ai-providers">
        <div className="overline text-primary mb-3">// {lang === "pt" ? "providers de ia" : "ai providers"}</div>
        <p className="text-xs text-white/60 mb-4 max-w-2xl">
          {lang === "pt"
            ? "Cada análise roda em subprocess isolado por usuário (HOME dedicado), sem API key direta. Claude usa OAuth (claude login); demais providers são acionados via CLI multi-provider transparentemente — você nunca precisa colar uma chave no Ratchet."
            : "Each analysis runs as an isolated per-user subprocess (dedicated HOME), with no direct API key. Claude uses OAuth (claude login); other providers are wired through a multi-provider CLI transparently — you never paste a key into Ratchet."}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            { name: "Anthropic Claude", auth: "claude login", recommended: true },
            { name: "OpenAI",           auth: "OAuth · CLI", recommended: false },
            { name: "Google Gemini",    auth: "OAuth · CLI", recommended: false },
            { name: "OpenRouter",       auth: "OAuth · CLI", recommended: false },
            { name: "DeepSeek",         auth: "OAuth · CLI", recommended: false },
            { name: "Groq",             auth: "OAuth · CLI", recommended: false },
            { name: "Mistral",          auth: "OAuth · CLI", recommended: false },
            { name: "GitHub Models",    auth: "/onboard-github", recommended: false },
            { name: "Codex OAuth",      auth: "ChatGPT sign-in", recommended: false },
            { name: "Xiaomi MiMo",      auth: "OAuth · CLI", recommended: false },
            { name: "Ollama",           auth: "local · no key", recommended: false },
            { name: "AWS Bedrock",      auth: "IAM · env vars", recommended: false },
            { name: "Google Vertex",    auth: "GCP · env vars", recommended: false },
          ].map((p) => (
            <div key={p.name} className={`px-2.5 py-2 text-[11px] border ${p.recommended ? "border-primary bg-primary/10" : "border-white/10"}`} data-testid={`bridge-provider-${p.name.toLowerCase().replace(/[ /]/g, "-")}`}>
              <div className="font-display font-bold leading-tight">{p.name}</div>
              <div className="font-mono text-[10px] text-white/50 leading-tight mt-0.5 truncate">{p.auth}</div>
              {p.recommended && <div className="text-[9px] text-primary font-mono mt-1">★ {lang === "pt" ? "recomendado" : "recommended"}</div>}
            </div>
          ))}
        </div>
      </div>

      {revealed && (
        <div className="surface p-5 mb-6 border-primary" data-testid="bridge-reveal">
          <div className="overline text-primary mb-2">
            {lang === "pt" ? "copie agora — não será exibida novamente" : "copy now — won't be shown again"}
          </div>
          <div className="flex gap-2 items-center font-mono text-xs">
            <code className="flex-1 bg-black/40 p-3 break-all">{revealed.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(revealed.key); toast.success("Copied"); }} className="btn-ghost py-2 px-3">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={create} className="surface p-5 mb-6 flex gap-3 items-end" data-testid="bridge-new-form">
          <div className="flex-1">
            <label className="overline block mb-1">{lang === "pt" ? "Nome (ex: local-mac, prod-vm)" : "Name (e.g. local-mac, prod-vm)"}</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="bridge-name-input" />
          </div>
          <button disabled={loading} className="btn-primary" data-testid="bridge-create-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "pt" ? "Gerar" : "Generate")}
          </button>
        </form>
      )}

      <div className="surface">
        {keys.length === 0 ? (
          <div className="p-12 text-center text-white/40 text-sm">
            <Server className="w-8 h-8 mx-auto mb-3 opacity-30" />
            {lang === "pt" ? "Nenhuma bridge key ainda." : "No bridge keys yet."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/10">
              <tr className="text-left">
                <th className="overline p-4">{lang === "pt" ? "Nome" : "Name"}</th>
                <th className="overline p-4">{lang === "pt" ? "Prefixo" : "Prefix"}</th>
                <th className="overline p-4">{lang === "pt" ? "Última atividade" : "Last seen"}</th>
                <th className="overline p-4">{lang === "pt" ? "Criada" : "Created"}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-white/5" data-testid={`bridge-row-${k.id}`}>
                  <td className="p-4 font-medium">{k.name}</td>
                  <td className="p-4 font-mono text-xs text-white/50">{k.key_prefix}…</td>
                  <td className="p-4 font-mono text-xs">
                    {k.last_seen_at ? (
                      <span className="text-emerald-400">{new Date(k.last_seen_at).toLocaleString()}</span>
                    ) : (
                      <span className="text-white/40">{lang === "pt" ? "nunca" : "never"}</span>
                    )}
                  </td>
                  <td className="p-4 font-mono text-xs text-white/40">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => del(k.id)} className="text-xs text-red-400 hover:underline inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> {lang === "pt" ? "revogar" : "revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
